// ============================================================
// NordicMapAnimation — 10-second ambient Nordic map loop.
// Faithful port of design/hero-animation/source/hero-anim.jsx
// (MapAnimation, mode="on-white"). Geometry is a static build-time
// snapshot (nordicGeometry.ts); the loop is driven by a single `t`.
// ============================================================
import {
  viewW,
  viewH,
  countryPaths,
  hexes,
  stockholmPx,
  territoryIndices,
  addressPoints,
} from "./nordicGeometry";

const clamp = (v: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const easeIn = (t: number) => t * t * t;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const sub = (t: number, start: number, end: number) =>
  clamp((t - start) / (end - start));
const typeUpTo = (s: string, p: number) => s.slice(0, Math.floor(p * s.length));

const SEARCH_TYPO = "Nordenflyktsv 74";
const SEARCH_FIX = "Nordenflychtsvägen 74";
const SEARCH_POST = "112 51 Stockholm";
const SEARCH_COORD = "59°20'N · 18°04'E";

// On-white color object (petrol == #00545e, coral == #ff6666 via tokens).
const C = {
  outline: "hsl(var(--petrol) / 0.45)",
  outlineSE: "hsl(var(--petrol) / 0.85)",
  landFill: "hsl(var(--petrol) / 0.04)",
  landFillSE: "hsl(var(--petrol) / 0.07)",
  hexFill: "hsl(var(--petrol) / 0.06)",
  hexStroke: "hsl(var(--petrol) / 0.22)",
  hexTerritoryStroke: "hsl(var(--petrol) / 0.45)",
  pin: "hsl(var(--coral))",
  pinRing: "hsl(var(--coral) / 0.55)",
  grid: "hsl(var(--petrol) / 0.06)",
  cardBg: "#fff",
  cardShadow: "0 18px 50px hsl(var(--petrol) / 0.18)",
  labelBg: "hsl(var(--petrol))",
  labelText: "#fff",
  connector: "hsl(var(--petrol))",
  addrText: "#666",
  addrTextBg: "rgba(255,255,255,0.85)",
  rejectColor: "hsl(var(--coral))",
} as const;

interface FloatingAddress {
  pt: [number, number];
  label: string;
  valid: boolean;
  appear: number;
  resolve: number;
}

const floatingAddresses: FloatingAddress[] = [
  { pt: addressPoints.umea, label: "Tallvägen 3, Umeå", valid: true, appear: 0.06, resolve: 0.18 },
  { pt: addressPoints.uppsala, label: "Storgatan 1, Uppsala", valid: true, appear: 0.1, resolve: 0.22 },
  { pt: addressPoints.linkoping, label: "Hamngatan 7, Linköping", valid: false, appear: 0.2, resolve: 0.32 },
  { pt: addressPoints.goteborg, label: "Avenyn 12, Göteborg", valid: true, appear: 0.24, resolve: 0.36 },
  { pt: addressPoints.oslo, label: "Karl Johans gate 4, Oslo", valid: true, appear: 0.38, resolve: 0.5 },
  { pt: addressPoints.copenhagen, label: "Strøget 22, København", valid: true, appear: 0.44, resolve: 0.56 },
  { pt: addressPoints.helsinki, label: "Aleksanterink. 9, Helsinki", valid: true, appear: 0.5, resolve: 0.62 },
  { pt: addressPoints.malmo, label: "Storgaten 99, Malmöö", valid: false, appear: 0.3, resolve: 0.44 },
];

const territorySet = new Set(territoryIndices);

export function NordicMapAnimation({ t }: { t: number }) {
  // ----- PHASE PROGRESS -----
  const p1 = sub(t, 0.0, 0.16); // outlines
  const p3 = sub(t, 0.34, 0.58); // mosaic
  const p4 = sub(t, 0.58, 0.8); // territory
  const p5 = sub(t, 0.94, 1.0); // fade reset

  const masterOpacity = 1 - p5;

  const outlineDraw = easeOut(p1);
  const outlineLen = 4200;
  const landFillOpacity = easeOut(sub(t, 0.1, 0.22));

  // Main validation card
  const cardIn = easeOut(sub(t, 0.18, 0.25));
  const cardOut = easeIn(sub(t, 0.78, 0.88));
  const cardOpacity = clamp(cardIn - cardOut);
  const cardY = lerp(20, 0, cardIn) - lerp(0, 16, cardOut);
  const typeP = sub(t, 0.2, 0.28);
  const typedTypo = typeUpTo(SEARCH_TYPO, typeP);
  const validated = t >= 0.29;
  const validatedAppear = easeOut(sub(t, 0.29, 0.34));

  // Pin
  const pinDrop = easeOut(sub(t, 0.3, 0.36));
  const pinPulse = sub(t, 0.36, 0.52);
  const pinFade = sub(t, 0.86, 0.94);
  const pinOpacity = pinDrop * (1 - pinFade);

  // Mosaic
  const mosaicReveal = easeOut(p3);

  // Territory
  const terrCount = territoryIndices.length;
  const labelIn = easeOut(sub(t, 0.7, 0.78));
  const labelOut = easeIn(sub(t, 0.9, 0.97));
  const labelOpacity = clamp(labelIn - labelOut);

  const tailFade = 1 - easeIn(sub(t, 0.9, 0.99));

  // Mälardalen label position in SVG coords — safely inside the viewBox.
  const LABEL_X = 218;
  const LABEL_Y = 410;
  const LABEL_W = 138;
  const LABEL_H = 64;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        opacity: masterOpacity,
        fontFamily: "Manrope, sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: `${viewW} / ${viewH}`,
          height: "100%",
          maxWidth: "100%",
        }}
      >
        <svg
          viewBox={`0 0 ${viewW} ${viewH}`}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <clipPath id="nordic-clip">
              {Object.values(countryPaths).map((d, i) => (
                <path key={i} d={d} />
              ))}
            </clipPath>
          </defs>

          {/* Ambient coordinate grid */}
          <g opacity="1">
            {Array.from({ length: 7 }).map((_, i) => {
              const x = (i + 1) * (viewW / 8);
              return <line key={"v" + i} x1={x} y1="0" x2={x} y2={viewH} stroke={C.grid} strokeWidth="0.5" />;
            })}
            {Array.from({ length: 9 }).map((_, i) => {
              const y = (i + 1) * (viewH / 10);
              return <line key={"h" + i} x1="0" y1={y} x2={viewW} y2={y} stroke={C.grid} strokeWidth="0.5" />;
            })}
          </g>

          {/* Country land fills + outlines */}
          {Object.entries(countryPaths).map(([name, d]) => {
            const isSE = name === "Sweden";
            return (
              <g key={name}>
                <path d={d} fill={isSE ? C.landFillSE : C.landFill} opacity={landFillOpacity} stroke="none" />
                <path
                  d={d}
                  fill="none"
                  stroke={isSE ? C.outlineSE : C.outline}
                  strokeWidth={isSE ? 1.2 : 0.9}
                  strokeDasharray={outlineLen}
                  strokeDashoffset={outlineLen * (1 - outlineDraw)}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                />
              </g>
            );
          })}

          {/* Hex mosaic clipped to all Nordic land */}
          <g clipPath="url(#nordic-clip)">
            {hexes.map((h, i) => {
              const yNorm = clamp(1 - h.y / viewH);
              const hexDelay = yNorm * 0.6;
              const hexP = clamp((mosaicReveal - hexDelay) / 0.35);
              const isTerr = territorySet.has(h.idx);

              let terrFill = 0;
              if (isTerr && p4 > 0) {
                const order = territoryIndices.indexOf(h.idx);
                terrFill = easeOut(clamp((p4 - (order / terrCount) * 0.55) / 0.45));
              }

              const fill = isTerr
                ? `hsl(var(--coral) / ${0.92 * terrFill * tailFade})`
                : C.hexFill;
              const stroke = isTerr && terrFill > 0.3 ? C.hexTerritoryStroke : C.hexStroke;

              return (
                <polygon
                  key={i}
                  points={h.points}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="0.3"
                  opacity={hexP * tailFade}
                />
              );
            })}
          </g>

          {/* Crisp inner outline pass on Sweden, on top of mosaic */}
          {countryPaths.Sweden && (
            <path d={countryPaths.Sweden} fill="none" stroke={C.outlineSE} strokeWidth="0.6" opacity={p1 * 0.7} />
          )}

          {/* Stockholm pin / reticle */}
          {pinOpacity > 0.01 && (
            <g transform={`translate(${stockholmPx[0]}, ${stockholmPx[1]})`} opacity={pinOpacity}>
              <circle r={4 + 16 * easeOut(pinPulse)} fill="none" stroke={C.pinRing} strokeWidth="0.8" opacity={1 - pinPulse} />
              <circle r={3 + 9 * easeOut(pinPulse)} fill="none" stroke={C.pinRing} strokeWidth="0.6" opacity={(1 - pinPulse) * 0.55} />
              <circle r="3.2" fill={C.pin} />
              <circle r="1.3" fill="#fff" />
              <line x1="-10" y1="0" x2="-6" y2="0" stroke={C.pin} strokeWidth="1" />
              <line x1="6" y1="0" x2="10" y2="0" stroke={C.pin} strokeWidth="1" />
              <line x1="0" y1="-10" x2="0" y2="-6" stroke={C.pin} strokeWidth="1" />
              <line x1="0" y1="6" x2="0" y2="10" stroke={C.pin} strokeWidth="1" />
            </g>
          )}

          {/* Floating address labels — appear/resolve at their own timings */}
          {floatingAddresses.map((a, i) => {
            const appearP = easeOut(sub(t, a.appear, a.appear + 0.04));
            const fadeP = sub(t, a.resolve + 0.18, a.resolve + 0.28);
            const op = clamp(appearP - fadeP) * tailFade;
            if (op < 0.02) return null;

            const resolveP = easeOut(sub(t, a.resolve, a.resolve + 0.06));
            const resolved = resolveP > 0.1;
            const isReject = !a.valid && resolved;
            const markerX = a.label.length * 5.4 + 19;

            return (
              <g key={"addr" + i} transform={`translate(${a.pt[0]}, ${a.pt[1]})`} opacity={op}>
                <circle
                  r="2.4"
                  fill={isReject ? C.rejectColor : C.pin}
                  opacity={resolved ? 1 : 0.4 + 0.6 * Math.abs(Math.sin(t * Math.PI * 8))}
                />
                <line x1="0" y1="0" x2="12" y2="-10" stroke={isReject ? C.rejectColor : "hsl(var(--petrol) / 0.4)"} strokeWidth="0.6" />
                <g transform="translate(12, -22)">
                  <rect x="0" y="0" width={a.label.length * 5.4 + 28} height="20" rx="3" fill={C.addrTextBg} opacity="0.97" />
                  <text
                    x="9"
                    y="13.5"
                    fontFamily="Manrope, ui-monospace, monospace"
                    fontSize="10.5"
                    fontWeight="600"
                    fill={C.addrText}
                    letterSpacing="-0.01em"
                  >
                    {a.label}
                  </text>
                  {resolved && isReject && (
                    <g transform={`translate(${markerX}, 10)`} opacity={resolveP}>
                      <circle r="4.4" fill={C.rejectColor} />
                      <line x1="-2.2" y1="-2.2" x2="2.2" y2="2.2" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                      <line x1="2.2" y1="-2.2" x2="-2.2" y2="2.2" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                    </g>
                  )}
                  {resolved && !isReject && (
                    <g transform={`translate(${markerX}, 10)`} opacity={resolveP}>
                      <circle r="4.4" fill="hsl(var(--petrol))" />
                      <path d="M-2 0 L-0.5 1.8 L2.2 -1.6" stroke="#fff" strokeWidth="1.3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                  )}
                </g>
              </g>
            );
          })}

          {/* Connector from pin to Mälardalen label */}
          {labelOpacity > 0 && (
            <g opacity={labelOpacity}>
              <line
                x1={stockholmPx[0]}
                y1={stockholmPx[1]}
                x2={LABEL_X + 6}
                y2={LABEL_Y + LABEL_H}
                stroke={C.connector}
                strokeWidth="0.9"
                strokeDasharray="2 2"
              />
            </g>
          )}

          {/* Mälardalen label — native SVG so it always fits */}
          {labelOpacity > 0.01 && (
            <g transform={`translate(${LABEL_X}, ${LABEL_Y})`} opacity={labelOpacity}>
              <rect x="0" y="0" width={LABEL_W} height={LABEL_H} rx="4" fill="hsl(var(--petrol))" />
              <text x="11" y="16" fontFamily="Manrope, sans-serif" fontSize="8" fontWeight="700" letterSpacing="1.8" fill="hsl(var(--coral))">
                SÄLJOMRÅDE
              </text>
              <text x="11" y="37" fontFamily="Manrope, sans-serif" fontSize="18" fontWeight="700" letterSpacing="-0.5" fill="#fff">
                Mälardalen
              </text>
              <line x1="11" y1="46" x2={LABEL_W - 11} y2="46" stroke="rgba(255,255,255,0.22)" strokeWidth="0.6" />
              <text x="11" y="58" fontFamily="Manrope, sans-serif" fontSize="8.5" fontWeight="500" fill="rgba(255,255,255,0.82)">
                14 omr.
              </text>
              <text x={LABEL_W - 11} y="58" fontFamily="Manrope, sans-serif" fontSize="8.5" fontWeight="500" textAnchor="end" fill="rgba(255,255,255,0.82)">
                2.1M adresser
              </text>
            </g>
          )}
        </svg>

        {/* HTML OVERLAY: Address validation card (main) */}
        {cardOpacity > 0.01 && (
          <div
            style={{
              position: "absolute",
              left: "1%",
              top: `calc(10% + ${cardY}px)`,
              width: "min(300px, 92%)",
              background: C.cardBg,
              borderRadius: 10,
              padding: "16px 18px",
              boxShadow: C.cardShadow,
              opacity: cardOpacity,
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.18em",
                color: "#666",
                textTransform: "uppercase",
                marginBottom: 8,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: validated ? "hsl(var(--petrol))" : "hsl(var(--coral))",
                  transition: "background 240ms",
                }}
              />
              {validated ? "VALIDERAD" : "SÖKNING"}
            </div>

            {!validated && (
              <div style={{ fontSize: 17, fontWeight: 500, color: "#323332", minHeight: 24 }}>
                {typedTypo}
                <span
                  style={{
                    display: "inline-block",
                    width: 2,
                    height: 18,
                    background: "hsl(var(--coral))",
                    marginLeft: 2,
                    verticalAlign: "-3px",
                    opacity: Math.floor(t * 24) % 2 ? 1 : 0,
                  }}
                />
              </div>
            )}

            {validated && (
              <div style={{ opacity: validatedAppear }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: "hsl(var(--petrol))", lineHeight: 1.2 }}>{SEARCH_FIX}</div>
                <div style={{ fontSize: 14, fontWeight: 400, color: "#666", marginTop: 2 }}>{SEARCH_POST}</div>
                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 10,
                    borderTop: "1px solid #f0f0f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.16em", color: "hsl(var(--coral))" }}>{SEARCH_COORD}</span>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="6.2" fill="hsl(var(--petrol))" />
                    <path d="M4 7.2 L6 9 L10 5" stroke="#fff" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
